import { useState, useCallback } from 'react';
import { getFaqSectionDocData } from '../api/faqSection';

function cleanWordHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function removeStyles(element) {
    if (element.hasAttribute('style')) {
      element.removeAttribute('style');
    }
    if (element.tagName === 'SPAN' && element.textContent.trim() === '') {
      element.remove();
      return;
    }
    Array.from(element.children).forEach(removeStyles);
  }

  removeStyles(doc.body);

  const spans = doc.body.querySelectorAll('span');
  spans.forEach(span => {
    if (span.textContent.trim() === '') {
      span.remove();
    }
  });

  const paragraphs = doc.body.querySelectorAll('p');

  paragraphs.forEach(p => {
    const hasText = p.textContent.trim() !== '';
    const hasMedia = p.querySelector('img, video, iframe, svg');

    if (hasMedia) {
      p.classList.add('media-container');
    }

    if (!hasText && !hasMedia) {
      p.remove();
    }
  });

  return doc.body.innerHTML;
}



export function useAnswerCache() {
  const [answers, setAnswers] = useState(new Map());
  const [loadingAnswers, setLoadingAnswers] = useState(new Set());

  const fetchAnswer = useCallback(async (questionId, culture) => {
    if (answers.has(questionId)) {
      return answers.get(questionId);
    }

    if (loadingAnswers.has(questionId)) {
      return null;
    }

    setLoadingAnswers(prev => new Set(prev).add(questionId));

    try {
      const htmlContent = await getFaqSectionDocData(questionId, culture);

      setAnswers(prev => {
        const newMap = new Map(prev);
        newMap.set(questionId, cleanWordHtml(htmlContent));
        return newMap;
      });

      return cleanWordHtml(htmlContent);
    } catch (error) {
      console.error('Failed to fetch answer:', error);
      return null;
    } finally {
      setLoadingAnswers(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionId);
        return newSet;
      });
    }
  }, [answers, loadingAnswers]);

  const getAnswer = useCallback((questionId) => {
    return answers.get(questionId);
  }, [answers]);

  const hasAnswer = useCallback((questionId) => {
    return answers.has(questionId);
  }, [answers]);

  const isLoading = useCallback((questionId) => {
    return loadingAnswers.has(questionId);
  }, [loadingAnswers]);

  return {
    fetchAnswer,
    getAnswer,
    hasAnswer,
    isLoading
  };
}
